import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Check, ChevronRight, Search, Shield, X } from "lucide-react";
import type { AccessCatalog, AccessUser } from "./api";

// Diálogo de permissões no padrão do Portal de Chamados (UserPermissionsDialog).

type ModuleFilter = "all" | "granted" | "changed";
type ModuleLevel = "none" | "partial" | "full";

const moduleFilters: { value: ModuleFilter; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "granted", label: "Com acesso" },
  { value: "changed", label: "Alterados" },
];

const levelLabels: Record<ModuleLevel, string> = {
  none: "Sem acesso",
  partial: "Acesso parcial",
  full: "Acesso total",
};

/** Busca sem acento e sem caixa ("conformidade" encontra "Não Conformidades"). */
function normalizeSearch(value: string) {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLocaleLowerCase("pt-BR").trim();
}

export function allPermissionIds(catalog?: AccessCatalog) {
  return catalog?.permissionGroups.flatMap((group) => group.items.map((item) => item.id)) || [];
}

function rolePermissionSet(catalog: AccessCatalog, role: string) {
  const base = catalog.rolePermissions[role] || [];
  return new Set(base.includes("*") ? allPermissionIds(catalog) : base);
}

function moduleLevel(granted: number, total: number): ModuleLevel {
  if (!granted) return "none";
  return granted === total ? "full" : "partial";
}

function plural(count: number, singular: string, pluralForm: string) {
  return `${count} ${count === 1 ? singular : pluralForm}`;
}

export function AccessPermissionsDialog({
  user,
  catalog,
  saving,
  onClose,
  onSave,
}: {
  user: AccessUser;
  catalog: AccessCatalog;
  saving: boolean;
  onClose: () => void;
  onSave: (role: string, permissions: Set<string>) => void;
}) {
  const initialPermissions = useMemo(
    () => new Set(user.permissions.includes("*") ? allPermissionIds(catalog) : user.permissions),
    [catalog, user],
  );
  const [role, setRole] = useState(user.role);
  const [permissions, setPermissions] = useState<Set<string>>(() => new Set(initialPermissions));
  const [selectedGroup, setSelectedGroup] = useState(catalog.permissionGroups[0]?.group || "");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ModuleFilter>("all");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const dialogRef = useRef<HTMLElement>(null);

  const roleLabel = (id: string) => catalog.roles.find((item) => item.id === id)?.label || id;
  const roleChanged = role !== user.role;
  const changedIds = useMemo(
    () => new Set(allPermissionIds(catalog).filter((id) => permissions.has(id) !== initialPermissions.has(id))),
    [catalog, initialPermissions, permissions],
  );
  const changedGroups = useMemo(
    () => new Set(catalog.permissionGroups.filter((group) => group.items.some((item) => changedIds.has(item.id))).map((group) => group.group)),
    [catalog, changedIds],
  );
  const hasChanges = changedIds.size > 0 || roleChanged;
  const changedLabel = [
    changedIds.size ? `${plural(changedIds.size, "permissão alterada", "permissões alteradas")}` : "",
    roleChanged ? `perfil: ${roleLabel(role)}` : "",
  ].filter(Boolean).join(" · ");

  const query = normalizeSearch(search);
  const filtering = Boolean(query) || filter !== "all";
  const visibleGroups = catalog.permissionGroups.filter((group) => {
    const matchesQuery =
      !query ||
      normalizeSearch(group.group).includes(query) ||
      group.items.some((item) => normalizeSearch(item.label).includes(query));
    if (!matchesQuery) return false;
    if (filter === "granted") return group.items.some((item) => permissions.has(item.id));
    if (filter === "changed") return changedGroups.has(group.group);
    return true;
  });
  const activeGroup = catalog.permissionGroups.find((group) => group.group === selectedGroup);
  const activeGranted = activeGroup?.items.filter((item) => permissions.has(item.id)).length || 0;
  const activeTotal = activeGroup?.items.length || 0;

  const requestClose = () => {
    if (saving) return;
    if (hasChanges) setConfirmDiscard(true);
    else onClose();
  };

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  useEffect(() => {
    const handle = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      if (confirmDiscard) setConfirmDiscard(false);
      else requestClose();
    };
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  });

  const changeRole = (nextRole: string) => {
    setRole(nextRole);
    setPermissions(rolePermissionSet(catalog, nextRole));
  };

  const togglePermission = (id: string, checked: boolean) => {
    setPermissions((values) => {
      const next = new Set(values);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const setGroupPermissions = (checked: boolean) => {
    if (!activeGroup) return;
    setPermissions((values) => {
      const next = new Set(values);
      for (const item of activeGroup.items) {
        if (checked) next.add(item.id);
        else next.delete(item.id);
      }
      return next;
    });
  };

  return (
    <div className="dialog-backdrop perm-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose(); }}>
      <section ref={dialogRef} className="perm-dialog" role="dialog" aria-modal="true" aria-labelledby="perm-title" aria-describedby="perm-email" tabIndex={-1}>
        <header className="perm-header">
          <div>
            <h2 id="perm-title">Gerenciar usuário e permissões</h2>
            <p id="perm-email">{user.email || user.name}</p>
          </div>
          <button type="button" className="perm-ghost-icon" aria-label="Fechar" disabled={saving} onClick={requestClose}><X /></button>
        </header>

        <section className="perm-user">
          <div className="perm-user-fields">
            <UserInfoField label="Nome completo" value={user.name || "—"} />
            <UserInfoField label="E-mail" value={user.email || "Não informado"} />
            <UserInfoField label="Perfil atual" value={roleLabel(user.role)} />
            <UserInfoField label="Permissões alteradas" value={hasChanges ? changedLabel : "Nenhuma alteração pendente"} />
          </div>
          <fieldset className="perm-roles" disabled={saving}>
            <legend>Perfil no SGQ</legend>
            <div role="radiogroup" aria-label="Perfil no SGQ">
              {catalog.roles.map((item) => {
                const base = catalog.rolePermissions[item.id] || [];
                const help = base.includes("*")
                  ? "Acesso total a todos os módulos."
                  : `${plural(rolePermissionSet(catalog, item.id).size, "permissão padrão", "permissões padrão")}.`;
                return (
                  <label key={item.id} className={role === item.id ? "is-selected" : ""}>
                    <input type="radio" name="perm-role" value={item.id} checked={role === item.id} onChange={() => changeRole(item.id)} />
                    <i aria-hidden="true" />
                    <span>
                      <strong>{item.label}</strong>
                      <small>{help}</small>
                    </span>
                  </label>
                );
              })}
            </div>
            <p>Trocar o perfil aplica as permissões padrão dele; depois você pode ajustar módulo a módulo.</p>
          </fieldset>
        </section>

        <div className="perm-body">
          <aside className="perm-modules">
            <div className="perm-modules-head">
              <span className="perm-eyebrow">Módulos do SGQ</span>
              {changedGroups.size > 0 && <span className="perm-badge is-changed">{plural(changedGroups.size, "alterado", "alterados")}</span>}
            </div>
            <label className="perm-search">
              <Search aria-hidden="true" />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar módulo ou permissão..." aria-label="Buscar módulo ou permissão" />
              {search && <button type="button" aria-label="Limpar busca" onClick={() => setSearch("")}><X /></button>}
            </label>
            <div className="perm-chips" role="group" aria-label="Filtrar módulos">
              {moduleFilters.map((item) => (
                <button key={item.value} type="button" aria-pressed={filter === item.value} className={filter === item.value ? "is-active" : ""} onClick={() => setFilter(item.value)}>{item.label}</button>
              ))}
            </div>
            <div className="perm-module-list" role="listbox" aria-label="Módulos">
              {visibleGroups.length === 0 ? (
                <p className="perm-empty">{filtering ? "Nenhum módulo encontrado." : "Nenhum módulo disponível."}</p>
              ) : visibleGroups.map((group) => {
                const granted = group.items.filter((item) => permissions.has(item.id)).length;
                const level = moduleLevel(granted, group.items.length);
                const selected = group.group === selectedGroup;
                return (
                  <button key={group.group} type="button" role="option" aria-selected={selected} className={selected ? "is-selected" : ""} onClick={() => setSelectedGroup(group.group)}>
                    <i className={`perm-dot is-${level}`} aria-hidden="true" />
                    <span>
                      <strong>{group.group}</strong>
                      <small>{levelLabels[level]} · {granted}/{group.items.length}</small>
                    </span>
                    {changedGroups.has(group.group) && <i className="perm-dot is-changed" aria-label="Alterado" />}
                    <ChevronRight className="perm-chevron" aria-hidden="true" />
                  </button>
                );
              })}
            </div>
            <div className="perm-legend">
              <span><i className="perm-dot is-full" />Acesso total</span>
              <span><i className="perm-dot is-partial" />Parcial</span>
              <span><i className="perm-dot is-changed" />Alterado</span>
            </div>
          </aside>

          <main className="perm-panel">
            {!activeGroup ? (
              <p className="perm-empty">Selecione um módulo para configurar permissões.</p>
            ) : (
              <>
                <header className="perm-panel-head">
                  <span className="perm-eyebrow">Permissões</span>
                  <div>
                    <h3>Permissões — {activeGroup.group}</h3>
                    <span className="perm-badge">{levelLabels[moduleLevel(activeGranted, activeTotal)]}</span>
                    {changedGroups.has(activeGroup.group) && <span className="perm-badge is-changed">Alterado</span>}
                  </div>
                </header>
                <div className="perm-panel-body">
                  <section className="perm-section">
                    <div className="perm-section-head">
                      <div>
                        <h4>Ações permitidas no módulo</h4>
                        <p>Selecione o que este usuário poderá fazer em {activeGroup.group}.</p>
                      </div>
                      <div className="perm-bulk">
                        <button type="button" disabled={saving || activeGranted === activeTotal} onClick={() => setGroupPermissions(true)}>Marcar todas</button>
                        <button type="button" disabled={saving || activeGranted === 0} onClick={() => setGroupPermissions(false)}>Desmarcar todas</button>
                      </div>
                    </div>
                    <div className="perm-options">
                      {activeGroup.items.map((item) => {
                        const checked = permissions.has(item.id);
                        return (
                          <label key={item.id} className={`${checked ? "is-selected" : ""} ${changedIds.has(item.id) ? "is-changed" : ""}`}>
                            <input type="checkbox" checked={checked} disabled={saving} onChange={(event) => togglePermission(item.id, event.target.checked)} />
                            <i aria-hidden="true">{checked && <Check />}</i>
                            <span>
                              <strong>{item.label}</strong>
                              <small>{checked ? "Permissão concedida para este usuário." : "Esta ação não estará disponível para o usuário."}</small>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </section>
                  <section className="perm-summary">
                    <span className="perm-eyebrow">Resumo deste módulo</span>
                    <dl>
                      <div><dt>Nível de acesso</dt><dd className={activeGranted ? "is-allowed" : ""}>{activeGranted > 0 && <Check />}{levelLabels[moduleLevel(activeGranted, activeTotal)]}</dd></div>
                      <div><dt>Permissões concedidas</dt><dd>{activeGranted} de {activeTotal}</dd></div>
                    </dl>
                  </section>
                </div>
              </>
            )}
          </main>
        </div>

        <footer className="perm-footer">
          <span>{hasChanges ? changedLabel : "Nenhuma alteração pendente."}</span>
          <div>
            <button className="button secondary" type="button" disabled={saving} onClick={requestClose}>Cancelar</button>
            <button className="button primary" type="button" disabled={saving || !hasChanges} onClick={() => onSave(role, permissions)}>
              {saving ? <span className="spinner" aria-hidden="true" /> : <Shield />}
              {saving ? "Salvando…" : "Salvar permissões"}
            </button>
          </div>
        </footer>
      </section>

      {confirmDiscard && (
        <div className="dialog-backdrop perm-confirm-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setConfirmDiscard(false); }}>
          <section className="perm-confirm" role="alertdialog" aria-modal="true" aria-labelledby="perm-confirm-title" aria-describedby="perm-confirm-text">
            <h3 id="perm-confirm-title"><AlertTriangle aria-hidden="true" />Descartar alterações?</h3>
            <p id="perm-confirm-text">Existem permissões alteradas que ainda não foram salvas. Ao sair, essas alterações serão descartadas.</p>
            <footer>
              <button className="button secondary" type="button" autoFocus onClick={() => setConfirmDiscard(false)}>Continuar editando</button>
              <button className="button danger" type="button" onClick={onClose}>Descartar</button>
            </footer>
          </section>
        </div>
      )}
    </div>
  );
}

function UserInfoField({ label, value }: { label: string; value: string }) {
  return (
    <div className="perm-field">
      <span className="perm-eyebrow">{label}</span>
      <strong title={value}>{value}</strong>
    </div>
  );
}
